import { useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { SOCKET_IO_URL } from '../constants/index.js';

const useMatchSocket = ({
    params,
    twitchId,
    globalPlayersRef,
    callbacks,
}) => {
    const socketRef = useRef(null);
    const dataTimeoutRef = useRef(null);
    const waitingForNextMatchRef = useRef(false);

    const callbackRefs = useRef(callbacks);
    callbackRefs.current = callbacks;

    const logOverlay = useCallback((message) => {
        console.log(`[Deadlock Match Overlay] ${message}`);
    }, []);

    const resetDataTimeout = useCallback(() => {
        if (dataTimeoutRef.current) {
            clearTimeout(dataTimeoutRef.current);
            dataTimeoutRef.current = null;
        }

        dataTimeoutRef.current = setTimeout(() => {
            waitingForNextMatchRef.current = true;
            callbackRefs.current.setWaitingForNextMatch(true);
            callbackRefs.current.clearAllPortraits();
            dataTimeoutRef.current = null;
        }, 25000);
    }, []);

    useEffect(() => {
        const getStreamerIdentifier = () => {
            if (params) {
                const twitchIdParam = params.get('twitch_id');
                const streamerId = params.get('streamer_id');
                const streamer = params.get('streamer');
                if (twitchIdParam) return twitchIdParam;
                if (streamerId) return streamerId;
                if (streamer) return streamer;
            }
            if (twitchId) return twitchId;
            return null;
        };

        const identifier = getStreamerIdentifier();
        if (!identifier) return;

        const transports = ['websocket', 'polling'];

        const socket = io(SOCKET_IO_URL, {
            transports,
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 10000,
            reconnectionAttempts: Infinity,
            timeout: 10000,
            forceNew: false,
            autoConnect: true
        });
        socketRef.current = socket;

        socket.on('connect', () => {
            logOverlay(`Connected Streamer ${identifier}`);
            socket.emit('join-streamer-room', identifier);
        });

        socket.io.on('reconnect', (attemptNumber) => {
            logOverlay(`Reconnected after ${attemptNumber} attempt(s). Rejoining room for ${identifier}`);
            socket.emit('join-streamer-room', identifier);
        });

        socket.io.on('reconnect_failed', () => {
            logOverlay(`Reconnection failed for ${identifier}. Socket is dead.`);
        });

        socket.on('connect_error', () => {});

        socket.on('disconnect', (reason) => {
            logOverlay(`Disconnected Streamer ${identifier} (${reason})`);
            if (dataTimeoutRef.current) {
                clearTimeout(dataTimeoutRef.current);
                dataTimeoutRef.current = null;
            }
            waitingForNextMatchRef.current = false;
            callbackRefs.current.setWaitingForNextMatch(false);
            callbackRefs.current.clearAllPortraits();
        });

        socket.on('game_end', (data) => {
            const matchId = data?.matchId ?? 'unknown';
            logOverlay(`Match Ended ${matchId}`);
            if (dataTimeoutRef.current) {
                clearTimeout(dataTimeoutRef.current);
                dataTimeoutRef.current = null;
            }
            waitingForNextMatchRef.current = true;
            callbackRefs.current.setWaitingForNextMatch(true);
            callbackRefs.current.setGameActive(false);
            callbackRefs.current.setOverlayVisible(false);
            callbackRefs.current.setActivePlayers({});
            const gp = globalPlayersRef.current;
            Object.keys(gp).forEach(k => delete gp[k]);
            callbackRefs.current.handleGameEnd(data);
            callbackRefs.current.sendInternalMessage('game_end', data);
        });

        socket.on('player-states', (playerStates) => {
            if (!playerStates || Object.keys(playerStates).length === 0) {
                return;
            }

            resetDataTimeout();

            if (waitingForNextMatchRef.current) {
                logOverlay('Match Started');
                waitingForNextMatchRef.current = false;
                callbackRefs.current.setWaitingForNextMatch(false);
            }

            const gp = globalPlayersRef.current;

            Object.values(playerStates).forEach(playerData => {
                if (!playerData || !playerData.entity_index) return;

                const playerSlot = playerData.slot_position || playerData.player_slot || playerData.entity_index;

                let playerTeam;
                if (playerData.team === 2 || playerData.team === 'amber') {
                    playerTeam = 'amber';
                } else if (playerData.team === 3 || playerData.team === 'sapphire') {
                    playerTeam = 'sapphire';
                } else {
                    playerTeam = 'amber';
                }

                const processedPlayer = {
                    ...playerData,
                    player_slot: playerSlot,
                    team: playerTeam,
                    assigned_lane: playerData.assigned_lane ?? playerData.original_assigned_lane ?? 0,
                    original_assigned_lane: playerData.original_assigned_lane ?? playerData.assigned_lane ?? 0,
                    slot_position: playerSlot
                };

                const existingPlayer = gp[playerSlot];
                const preservedHeroData = existingPlayer ? {
                    hero_image: existingPlayer.hero_image,
                    hero_name: existingPlayer.hero_name
                } : {};

                const finalProcessedPlayer = {
                    ...processedPlayer,
                    ...preservedHeroData
                };

                gp[playerSlot] = finalProcessedPlayer;
                callbackRefs.current.setActivePlayers(prev => ({
                    ...prev,
                    [playerSlot]: finalProcessedPlayer
                }));

                if (finalProcessedPlayer.hero_id && finalProcessedPlayer.hero_id !== 'N/A' && !finalProcessedPlayer.hero_image) {
                    callbackRefs.current.loadHeroImage(finalProcessedPlayer.hero_id).then(heroInfo => {
                        if (heroInfo) {
                            const updatedPlayer = {
                                ...gp[playerSlot],
                                hero_name: heroInfo.name,
                                hero_image: heroInfo.imageUrl
                            };
                            gp[playerSlot] = updatedPlayer;
                            callbackRefs.current.setActivePlayers(prev => ({
                                ...prev,
                                [playerSlot]: updatedPlayer
                            }));
                        }
                    });
                }

                if (finalProcessedPlayer.upgrades && Array.isArray(finalProcessedPlayer.upgrades) && finalProcessedPlayer.upgrades.length > 0) {
                    callbackRefs.current.loadItemsForPlayer(finalProcessedPlayer.upgrades).then(items => {
                        if (items && items.length > 0) {
                            const updatedPlayer = {
                                ...gp[playerSlot],
                                items: items
                            };
                            gp[playerSlot] = updatedPlayer;
                            callbackRefs.current.setActivePlayers(prev => ({
                                ...prev,
                                [playerSlot]: updatedPlayer
                            }));
                        }
                    });
                }
            });

            callbackRefs.current.setGameActive(true);
            callbackRefs.current.setOverlayVisible(true);
        });

        socket.on('live-event', (event) => {
            if (!event || !event.event_name) return;

            const eventName = String(event.event_name);

            if (eventName === 'end' || eventName === 'game_end') {
                const matchId = event?.matchId ?? event?.match_id ?? 'unknown';
                logOverlay(`Match Ended ${matchId}`);
                if (dataTimeoutRef.current) {
                    clearTimeout(dataTimeoutRef.current);
                    dataTimeoutRef.current = null;
                }
                waitingForNextMatchRef.current = true;
                callbackRefs.current.setWaitingForNextMatch(true);
                callbackRefs.current.handleGameEnd(event);
                callbackRefs.current.sendInternalMessage('game_end', event);
                return;
            }

            resetDataTimeout();

            if (waitingForNextMatchRef.current) {
                if (eventName !== 'end' && eventName !== 'game_end') {
                    logOverlay('Match Started');
                    waitingForNextMatchRef.current = false;
                    callbackRefs.current.setWaitingForNextMatch(false);
                    callbackRefs.current.setGameActive(true);
                    callbackRefs.current.setOverlayVisible(true);
                } else {
                    return;
                }
            }

            const playerSlot = event.slot_position || event.player_slot || event.entity_index;
            const gp = globalPlayersRef.current;

            if (eventName === 'player_controller_entity_created' || eventName === 'player_pawn_entity_created') {
                if (playerSlot) {
                    callbackRefs.current.handlePlayerCreate({
                        ...event,
                        player_slot: playerSlot,
                        slot_position: playerSlot
                    });
                    callbackRefs.current.sendInternalMessage('player_create', event);
                }
            } else if (
                eventName === 'player_controller_entity_updated' ||
                eventName === 'player_controller_entity_update' ||
                eventName === 'player_pawn_entity_updated' ||
                eventName === 'player_pawn_entity_update'
            ) {
                if (playerSlot) {
                    callbackRefs.current.handlePlayerUpdate({
                        ...event,
                        player_slot: playerSlot,
                        slot_position: playerSlot
                    });
                    callbackRefs.current.sendInternalMessage('player_update', event);
                }
            } else if (eventName === 'player_controller_entity_deleted' || eventName === 'player_pawn_entity_deleted') {
                if (playerSlot && gp[playerSlot]) {
                    delete gp[playerSlot];
                    callbackRefs.current.setActivePlayers(prev => {
                        const newState = { ...prev };
                        delete newState[playerSlot];
                        return newState;
                    });
                }
            }
        });

        socket.on('error', () => {});

        return () => {
            if (dataTimeoutRef.current) {
                clearTimeout(dataTimeoutRef.current);
                dataTimeoutRef.current = null;
            }
            if (socketRef.current) {
                socketRef.current.emit('leave-streamer-room', identifier);
                socketRef.current.disconnect();
            }
        };
    }, [twitchId, params, logOverlay]);

    return { socketRef, waitingForNextMatchRef, dataTimeoutRef, resetDataTimeout };
};

export default useMatchSocket;
